import React, { useState } from "react";
import { motion } from "framer-motion";
import { HelpCircle, Settings, X, Copy, Check } from "lucide-react";
import { CONSTANTS } from "../../data";

interface SettingsModalProps {
  show: boolean;
  onClose: () => void;
  musicVolume: number;
  sfxVolume: number;
  customSeed: string;
  onMusicVolumeChange: (v: number) => void;
  onSfxVolumeChange: (v: number) => void;
  onCustomSeedChange: (v: string) => void;
  onApplySeed: () => void;
  onResetDailies: () => void;
  onShowResetConfirm: () => void;
  onPrestige: () => void;
  canPrestige: boolean;
  prestigeLevel: number;
  tutorialState: any;
  startTutorial: (tutorialId: string) => void;
  onCopyShareCode: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  show,
  onClose,
  musicVolume,
  sfxVolume,
  customSeed,
  onMusicVolumeChange,
  onSfxVolumeChange,
  onCustomSeedChange,
  onApplySeed,
  onResetDailies,
  onShowResetConfirm,
  onPrestige,
  canPrestige,
  prestigeLevel,
  tutorialState,
  startTutorial,
  onCopyShareCode,
}) => {
  const [shareSuccess, setShareSuccess] = useState(false);
  const [seedError, setSeedError] = useState("");

  const handleShareGame = async () => {
    try {
      await onCopyShareCode();
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 2000);
    } catch (error) {
      console.error("Failed to share game:", error);
    }
  };

  const handleApplySeed = () => {
    if (!customSeed || !customSeed.trim()) {
      setSeedError("Please enter a seed");
      setTimeout(() => setSeedError(""), 3000);
      return;
    }

    try {
      onApplySeed();
      setSeedError("");
    } catch (error) {
      setSeedError("Invalid seed format");
      setTimeout(() => setSeedError(""), 3000);
    }
  };

  const handleSeedChange = (value: string) => {
    onCustomSeedChange(value);
    if (seedError) {
      setSeedError("");
    }
  };

  if (!show) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-2 sm:p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-surface rounded-lg w-full max-w-md max-h-[95vh] sm:max-h-[90vh] flex flex-col shadow-xl border border-border-color"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 sm:p-4 border-b border-border-color flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg sm:text-xl font-bold text-text-primary flex items-center gap-2"><Settings /> Settings</h2>
          <button 
            onClick={onClose} 
            className="text-text-secondary hover:text-text-primary p-2 rounded-full hover:bg-bg-main min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>
        <div className="modal-content p-3 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
          <div>
            <label className="block mb-2 font-semibold text-text-primary text-sm sm:text-base">🎵 Music Volume</label>
            <input 
              type="range" 
              min="0" 
              max="1" 
              step="0.05" 
              value={musicVolume} 
              onChange={e => onMusicVolumeChange(parseFloat(e.target.value))} 
              className="w-full h-8" 
            />
            <div className="text-sm text-text-secondary mt-1 text-right">{Math.round(musicVolume * 100)}%</div>
          </div>
          <div>
            <label className="block mb-2 font-semibold text-text-primary text-sm sm:text-base">🔊 SFX Volume</label>
            <input 
              type="range" 
              min="0" 
              max="1" 
              step="0.05" 
              value={sfxVolume} 
              onChange={e => onSfxVolumeChange(parseFloat(e.target.value))} 
              className="w-full h-8" 
            />
            <div className="text-sm text-text-secondary mt-1 text-right">{Math.round(sfxVolume * 100)}%</div>
          </div>
          <div className="border-t border-border-color pt-4 sm:pt-6">
            <h3 className="font-semibold mb-2 text-text-primary text-sm sm:text-base">🎲 Share Your Progress</h3>
            <button
              onClick={handleShareGame}
              className={`w-full font-bold py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 min-h-[44px] text-sm sm:text-base ${
                shareSuccess 
                  ? "bg-success text-white" 
                  : "bg-primary-action text-white hover:bg-accent"
              }`}
            >
              {shareSuccess ? <Check size={20} /> : <Copy size={20} />}
              {shareSuccess ? "Link Copied!" : "Share Game"}
            </button>
            <p className="text-xs text-text-secondary mt-2 text-center">
              Generate a link to share your current progress with friends.
            </p>
          </div>
          <div className="border-t border-border-color pt-4 sm:pt-6">
            <h3 className="font-semibold mb-2 text-text-primary text-sm sm:text-base">🔗 Load from Seed</h3>
            <div className="space-y-2">
              <input
                type="text"
                value={customSeed}
                onChange={(e) => handleSeedChange(e.target.value)}
                placeholder="Enter seed code..."
                className={`w-full px-3 py-3 border rounded-lg text-sm bg-bg-main min-h-[44px] ${
                  seedError ? 'border-danger' : 'border-border-color focus:border-primary-action'
                } transition-colors focus:outline-none focus:ring-2 focus:ring-primary-action/50`}
              />
              {seedError && (
                <p className="text-danger text-xs">{seedError}</p>
              )}
              <button
                onClick={handleApplySeed}
                disabled={!customSeed?.trim()}
                className="w-full bg-success text-white font-bold py-3 rounded-lg hover:opacity-90 disabled:bg-gray-400 disabled:cursor-not-allowed min-h-[44px] text-sm sm:text-base"
              >
                Apply Seed
              </button>
            </div>
            <p className="text-xs text-text-secondary mt-2">
              Load game state from a seed code or share link.
            </p>
          </div>
          <div className="border-t border-border-color pt-4 sm:pt-6">
            <h3 className="font-semibold mb-2 flex items-center gap-2 text-text-primary text-sm sm:text-base"><HelpCircle size={20} /> How to Play</h3>
            <div className="space-y-2">
              {Object.values(CONSTANTS.TUTORIALS).map((tutorial: any) => {
                const isCompleted = tutorialState.completedTutorials.includes(tutorial.id);
                const isSkipped = tutorialState.skippedTutorials.includes(tutorial.id);
                return (
                  <div key={tutorial.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-bg-main p-3 rounded-lg border border-border-color gap-2">
                    <span className="text-sm text-text-secondary flex-1">{tutorial.title}</span>
                    <div className="flex items-center gap-2 justify-between sm:justify-end">
                      {isCompleted && <span className="text-xs text-success font-bold">Done</span>}
                      {isSkipped && <span className="text-xs text-text-secondary">Skipped</span>}
                      <button
                        onClick={() => startTutorial(tutorial.id)}
                        className="px-3 py-2 rounded text-xs bg-primary-action text-white hover:bg-accent transition-colors min-h-[36px] flex-shrink-0"
                      >
                        Replay
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="pt-4 sm:pt-6 border-t border-border-color">
            <h3 className="font-semibold mb-2 text-text-primary text-sm sm:text-base">Prestige</h3>
            <button
              onClick={onPrestige}
              disabled={!canPrestige}
              className="w-full bg-purple-600 text-white font-bold py-3 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed min-h-[44px] text-sm sm:text-base"
            >
              {canPrestige ? `Prestige to Level ${CONSTANTS.PRESTIGE.LEVELS[prestigeLevel + 1]?.ui || ''}` : `Req: LvL 10, 2 Legendaries`}
            </button>
            <p className="text-xs text-text-secondary mt-1">Reset progress for permanent perks. Current Prestige: {prestigeLevel > 0 ? CONSTANTS.PRESTIGE.LEVELS[prestigeLevel].ui : '0'}</p>
          </div>
          <div className="pt-4 sm:pt-6 border-t border-border-color">
            <h3 className="font-semibold mb-2 text-danger text-sm sm:text-base">Danger Zone</h3>
            <div className="space-y-2">
              <button 
                onClick={onResetDailies} 
                className="w-full bg-warning text-white font-bold py-3 rounded-lg hover:opacity-90 min-h-[44px] text-sm sm:text-base"
              >
                Reset Daily/Seeds
              </button>
              <button 
                onClick={onShowResetConfirm} 
                className="w-full bg-danger text-white font-bold py-3 rounded-lg hover:opacity-90 min-h-[44px] text-sm sm:text-base"
              >
                Reset Full Game
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SettingsModal;
